'use client';

import { useMemo, type FC, useContext, useState } from 'react';
import styles from './LeftNavClient.module.css';
import { UserContext } from '@/_contexts/UserProvider';
import { getClasses } from '@/_utils/classnames';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { LeftNavItem, type LeftNavItemProps } from '@/_components/LeftNavItem';
import { Flex, ErrorBoundary } from '@/_components';
import { usePathname } from 'next/navigation';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ClientConversationsContext } from '@/_contexts/client/ClientConversationsProvider';
import { LeftNavEntitySelector } from './_components/LeftNavEntitySelector';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { LeftNavClientProps } from './LeftNavClient.types';

export const LeftNavClient: FC<LeftNavClientProps> = ({ isPreview }) => {
  const { selectedCompanyHash, selectedClientCompany } =
    useContext(UserContext);
  const {
    previewUserCompanyHash,
    previewUser,
    previewTemplate,
    previewConversation,
  } = useContext(UserPreviewContext);
  const { colorPrimary, preferences } = useContext(FirmSettingsContext);
  const { currentEntityUnseenCount } = useContext(ClientConversationsContext);
  const { isCentered, isPublic } = useContext(LayoutContext);

  const companyHash: string | undefined = useMemo(
    () => (isPreview ? previewUserCompanyHash : selectedCompanyHash),
    [isPreview, previewUserCompanyHash, selectedCompanyHash],
  );

  const linkPrefix: string = isPreview
    ? `/preview/user/${previewUser?.id}`
    : '';

  const [isOpen, setOpen] = useState<boolean>(false);
  const path = usePathname();

  const onCloseEntities = () => setOpen(false);

  const asideClasses: string = getClasses(
    styles.leftNavClient,
    'flex gap-5 flex-column',
  );

  const relevantConversationSuffix: string = useMemo(
    () =>
      isPreview
        ? typeof previewConversation?.id === 'number'
          ? `/${previewConversation?.id}`
          : ''
        : typeof selectedClientCompany?.relevant_conversation_id === 'number'
          ? `/${selectedClientCompany?.relevant_conversation_id}`
          : '',
    [
      isPreview,
      selectedClientCompany?.relevant_conversation_id,
      previewConversation?.id,
    ],
  );

  if (isCentered || isPublic) return null;

  const LEFT_NAVIGATION_ITEMS: Array<LeftNavItemProps> = [
    {
      name: 'Dashboard',
      icon: 'circles-three',
      href: `${linkPrefix}/`,
      isActive: path === `${linkPrefix}/` || path === linkPrefix,
    },
    ...(!preferences?.communication_disabled
      ? [
          {
            name:
              preferences?.custom_messages_tile_name_for_clients || 'Messages',
            icon: 'chats',
            notificationCount: currentEntityUnseenCount,
            href: `${linkPrefix}/inbox/${companyHash}${relevantConversationSuffix}`,
            isActive: path.startsWith(`${linkPrefix}/inbox`),
          },
        ]
      : ([] as Array<any>)),
    ...(!preferences.hide_documents_view_from_clients
      ? [
          {
            name: 'Documents',
            icon: 'files',
            href: `${linkPrefix}/documents/${companyHash}`,
            isActive: path.startsWith(`${linkPrefix}/documents`),
          },
        ]
      : ([] as Array<any>)),
    {
      name: 'Profile Settings',
      icon: 'user-circle-gear',
      href: `${linkPrefix}/profile`,
      isActive: path === `${linkPrefix}/profile`,
    },
  ].filter(Boolean);

  const customAsideStyle: object = colorPrimary ? {} : {};

  return (
    <aside
      className={asideClasses}
      style={customAsideStyle}
      onMouseLeave={onCloseEntities}
      data-hide-on-print="true"
    >
      <Flex isColumn={true} gap={6}>
        <ErrorBoundary>
          <LeftNavEntitySelector isOpen={isOpen} setOpen={setOpen} />
        </ErrorBoundary>
        <Flex isColumn={true} gap={6}>
          {companyHash &&
            LEFT_NAVIGATION_ITEMS.map((item, key) => {
              return <LeftNavItem {...item} key={key} />;
            })}
          {!companyHash && isPreview && previewTemplate?.name && (
            <LeftNavItem name="Back" icon="arrow-left" href="/" />
          )}
        </Flex>
      </Flex>
    </aside>
  );
};
